/**
 * Secondary Navigation Component
 *
 * scroll-spy functionality to highlight the current section.
 * Includes both mobile (collapsible) and desktop layouts.
 *
 * @component SecondaryNav
 * @extends HTMLElement
 */
function doSecondaryNav() {
  if (customElements.get('secondary-nav')) return

  // Shopify 스타일 handleize 유틸 (소문자, 공백/언더스코어 -> -, 영숫자/하이픈만)
  function handleize(str) {
    return String(str || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '') // accents
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9\s-_]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
  }

  class SecondaryNav extends HTMLElement {
    constructor() {
      super()
      this.sections = []
      this.navItems = []
      this.lastScrollTop = 0
      this.header = document.querySelector('header')
      this.announcementBar = document.querySelector('.announcement-bar-section')
      this.sectionElement = this.closest('.secondary-nav')
      this.handleResizeOrPosition = this.handleResizeOrPosition.bind(this)
      this.jumpNavTrigger = this.querySelector('.jump-nav__trigger')
      this.init()
    }

    // 공통 오프셋 계산 (헤더 + 공지바 + 점프바)
    getScrollOffset() {
      let offset = 0
      if (this.announcementBar && this.announcementBar.offsetHeight > 0) {
        offset += this.announcementBar.offsetHeight
      }
      if (this.header && this.header.offsetHeight > 0) {
        offset += this.header.offsetHeight
      }
      if (this.sectionElement && this.sectionElement.offsetHeight > 0) {
        offset += this.sectionElement.offsetHeight
      }
      return offset + 8 // 살짝 여유
    }

    // 대상 id 요소로 이동(+아코디언 열기)
    openAndScrollTo(targetId) {
      const target = document.getElementById(targetId)
      if (!target) return false

      const details = target.closest('details')
      if (details && !details.open) details.open = true

      // details 열림 애니 고려
      setTimeout(() => {
        const rect = target.getBoundingClientRect()
        const y =
          (window.pageYOffset || document.documentElement.scrollTop) +
          rect.top -
          this.getScrollOffset()
        window.scrollTo({ top: y, behavior: 'smooth' })
      }, 250)

      return true
    }

    // 링크 정규화 (빈 href 보정, data-custom-section-id를 #id로)
    normalizeLinksAndUnhide() {
      const items = Array.from(this.querySelectorAll('.jump-nav__item'))
      items.forEach((li) => {
        // inline display:none 복구
        if (li.style && li.style.display === 'none')
          li.style.removeProperty('display')
        if (li.style && (li.style.visibility === 'hidden' || li.style.opacity === '0')) {
          li.style.removeProperty('visibility')
          li.style.removeProperty('opacity')
        }

        const a = li.querySelector('.jump-nav__link')
        if (!a) return

        let href = a.getAttribute('href') || ''
        let cid = li.getAttribute('data-custom-section-id') || ''
        cid = handleize(cid)

        // href가 비었거나 유효하지 않으면 data-custom-section-id로 보정
        if ((!href || href === '#') && cid) {
          href = '#' + cid
          a.setAttribute('href', href)
        }

        // data-custom-section-id 정규화
        if (cid && li.getAttribute('data-custom-section-id') !== cid) {
          li.setAttribute('data-custom-section-id', cid)
        }
      })
    }

    // 현재 id 기준으로 데스크탑/모바일 링크에 active 토글
    bindActiveById(id) {
      const allLinks = [
        ...Array.from(this.querySelectorAll('.jump-nav__menu-desktop .jump-nav__link')),
        ...Array.from(this.querySelectorAll('.jump-nav__menu .jump-nav__link')),
      ]
      const targetHref = '#' + id
      allLinks.forEach((a) => {
        const h = a.getAttribute('href') || ''
        a.classList.toggle('active', h === targetHref)
      })
    }

    init() {
      // 네비 아이템 수집
      this.navItems = Array.from(this.querySelectorAll('.jump-nav__item'))

      // 숨김 복구 + href 보정
      this.normalizeLinksAndUnhide()

      this.navLinksDesktop = Array.from(
        this.querySelectorAll('.jump-nav__menu-desktop .jump-nav__link')
      )
      this.navLinksMobile = Array.from(
        this.querySelectorAll('.jump-nav__menu .jump-nav__link')
      )

      // 처음 로드 시 active 없으면 첫 항목 강제 활성화(데스크탑/모바일 둘 다)
      if (this.navLinksDesktop.length && !this.navLinksDesktop.some(l => l.classList.contains('active'))) {
        this.navLinksDesktop[0].classList.add('active')
      }
      if (this.navLinksMobile.length && !this.navLinksMobile.some(l => l.classList.contains('active'))) {
        this.navLinksMobile[0].classList.add('active')
      }

      // 섹션 목록 수집 (#id로만)
      this.sections = this.navLinksDesktop
        .map((item) => {
          const href = item.getAttribute('href') || ''
          if (href.startsWith('#')) {
            const sectionId = href.substring(1)
            return document.getElementById(sectionId)
          }
          return null
        })
        .filter(Boolean)

      this.scrollSpy(this.sections, this.navLinksDesktop)
      this.scrollSpy(this.sections, this.navLinksMobile)

      // robust scroll to top
      const scrollToTopLoop = () => {
        let attempts = 0
        function loop() {
          window.scrollTo({ top: 0, behavior: 'smooth' })
          attempts++
          if (window.scrollY > 0 && attempts < 10) requestAnimationFrame(loop)
        }
        loop()
      }

      // 같은 페이지 앵커 클릭 가로채기
      this.querySelectorAll('.jump-nav__link[href^="#"]').forEach((link) => {
        link.addEventListener('click', (e) => {
          const href = link.getAttribute('href')
          if (!href || href.length < 2) return
          const targetId = href.substring(1)

          // top-top 처리
          const parentItem = link.closest('.jump-nav__item')
          if (parentItem && parentItem.getAttribute('data-top-top') === 'true') {
            e.preventDefault()
            // 클릭 즉시 active 이동
            this.bindActiveById(targetId)
            scrollToTopLoop()
            this.jumpNavTrigger?.click()
            return
          }

          // 클릭 즉시 active 이동 (스크롤 기다리지 않음)
          this.bindActiveById(targetId)

          const ok = this.openAndScrollTo(targetId)
          if (ok) {
            e.preventDefault()
            // 모바일 모달 닫기
            this.jumpNavTrigger?.click()
            // 주소창 해시 유지(뒤로가기 스택 과도 증가 방지)
            try {
              history.replaceState(null, '', '#' + targetId)
            } catch (err) {}
          }
        })
      })

      // 위치 보정
      this.handleResizeOrPosition()
      window.addEventListener('resize', this.handleResizeOrPosition)
      if (this.announcementBar) {
        this.announcementBarResizeObserver = new ResizeObserver(() => {
          this.updatePosition()
        })
        this.announcementBarResizeObserver.observe(this.announcementBar)
      }

      // 로그/여백 보정
      this.logSectionInfo()

      // 스크롤 바인딩
      this.onScroll = this.onScroll.bind(this)
      window.addEventListener('scroll', this.onScroll, { passive: true })

      // 초기 위치/상태
      this.updatePosition()
      this.onScroll()

      // 해시 진입/변경 처리: active 동기화 포함
      if (location.hash && location.hash.length > 1) {
        const id = decodeURIComponent(location.hash.slice(1))
        this.bindActiveById(id)
        this.openAndScrollTo(id)
      }
      window.addEventListener('hashchange', () => {
        const id = decodeURIComponent(location.hash.slice(1))
        this.bindActiveById(id)
        this.openAndScrollTo(id)
      })
    }

    // (참고용) 예전 숨김 로직
    hideNavItemIfSectionDoesNotExist(navItems) {
      navItems.forEach((item) => {
        const customScrollToIdRaw = item.dataset.customSectionId || ''
        const customScrollToId = handleize(customScrollToIdRaw)
        const customScrollToIdElement = document.getElementById(customScrollToId)
        // if (!customScrollToIdElement) item.style.display = 'none'
      })
    }

    handleResizeOrPosition() {
      if (!this.sectionElement) return
      let offset = 0
      if (this.announcementBar && this.announcementBar.offsetHeight > 0) {
        offset += this.announcementBar.offsetHeight
      }
      if (this.header && this.header.offsetHeight > 0) {
        offset += this.header.offsetHeight
      }
      this.sectionElement.style.top = `${offset}px`
    }

    logSectionInfo() {
      requestAnimationFrame(() => {
        if (this.sectionElement) {
          const sectionHeight = this.sectionElement.offsetHeight
          const nextSection = this.sectionElement.nextElementSibling
          if (nextSection) {
            const computedStyle = window.getComputedStyle(nextSection)
            const currentPadding = parseFloat(computedStyle.paddingTop) || 0
            const newPadding = currentPadding + sectionHeight
            nextSection.style.paddingTop = `${newPadding}px`
          }
        }
      })
    }

    updatePosition() {
      if (!this.sectionElement) return

      let announcementBarHeight = 0
      if (this.announcementBar && this.announcementBar.offsetHeight > 0) {
        announcementBarHeight = this.announcementBar.offsetHeight
      }

      let headerHeight = 0
      let headerIsVisible = true
      if (this.header) {
        headerIsVisible = !this.header.classList.contains('shopify-section-header-hidden')
        if (headerIsVisible && this.header.offsetHeight > 0) {
          headerHeight = this.header.offsetHeight
        }
      }

      const currentScrollTop =
        window.pageYOffset || document.documentElement.scrollTop
      const isScrollingDown = currentScrollTop > this.lastScrollTop

      if (isScrollingDown) {
        this.sectionElement.style.top = `${announcementBarHeight}px`
      } else {
        this.sectionElement.style.top = `${announcementBarHeight + (headerIsVisible ? headerHeight : 0)}px`
      }

      this.lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop
    }

    onScroll() {
      // 위치 업데이트
      this.updatePosition()

      // is-scrolled 토글 (보더/스타일용)
      if (window.scrollY > 0) {
        this.sectionElement.classList.add('is-scrolled')
      } else {
        this.sectionElement.classList.remove('is-scrolled')
      }
    }

    scrollSpy(sections, menu_links) {
      const makeActive = (link) => menu_links[link].classList.add('active')
      const removeActive = (link) => menu_links[link].classList.remove('active')
      const removeAllActive = () =>
        [...Array(sections.length).keys()].forEach((link) => removeActive(link))

      // 헤더/바 높이를 반영한 전환 시점
      const sectionMargin = this.getScrollOffset() + 60
      let currentActive = 0

      const updateActive = () => {
        const current =
          sections.length -
          [...sections]
            .reverse()
            .findIndex(
              (section) => window.scrollY >= section.offsetTop - sectionMargin
            ) -
          1
        if (current !== currentActive && current >= 0 && current < sections.length) {
          removeAllActive()
          currentActive = current
          makeActive(current)
        }
      }

      window.addEventListener('scroll', updateActive)
      updateActive() // 초기 1회
    }
  }

  customElements.define('secondary-nav', SecondaryNav)
}

doSecondaryNav()
